class HomeHeroComponent extends HTMLElement {
  constructor() {
    super();
    this.checkAnnouncement = this.checkAnnouncement.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.animateLoop = this.animateLoop.bind(this);
    this.shuffleImage = this.shuffleImage.bind(this);

    // Mouse tracking
    this.mouse = { x: 0, y: 0 };
    this.current = { x: 0, y: 0 };

    // Lerp factor (0-1, lower = smoother/slower)
    this.lerpFactor = 0.1;

    // Animation
    /** @type {number | null} */
    this.animationFrameId = null;
    /** @type {number | null} */
    this.shuffleIntervalId = null;

    // Images
    /** @type {Array<{url: string, srcset: string, alt: string}>} */
    this.images = [];
    this.currentImageIndex = 0;

    // Elements
    /** @type {HTMLElement | null} */
    this.floatingImage = null;
    /** @type {HTMLImageElement[]} */
    this.imageElements = [];
  }
  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    this.checkAnnouncement();

    // Parse images data
    const imagesData = this.getAttribute('data-images');
    if (imagesData) {
      try {
        this.images = JSON.parse(imagesData);
      } catch (e) {
        console.error('Failed to parse images data:', e);
        return;
      }
    }

    if (this.images.length === 0) return;

    // Get the floating image container
    this.floatingImage = this.querySelector('.home-hero__floating-image');
    if (!this.floatingImage) return;

    // Create all image elements and layer them
    this.createImageLayers();

    // Set initial random position
    this.setRandomPosition();

    // Start mouse/touch tracking (mouse on document, touch on element)
    document.addEventListener('mousemove', this.onMouseMove);
    this.addEventListener('touchmove', this.onTouchMove, { passive: true });

    // Start animation loop
    this.animateLoop();

    // Auto-shuffle image every second
    this.shuffleIntervalId = setInterval(this.shuffleImage, 3000);

    // Listen for theme editor updates
    if (Shopify?.designMode) {
      document.addEventListener('shopify:section:load', this.checkAnnouncement);
      document.addEventListener('shopify:section:unload', this.checkAnnouncement);
    }
  }

  createImageLayers() {
    if (!this.floatingImage) return;

    // Clear existing content
    this.floatingImage.innerHTML = '';

    // Create an image element for each image
    this.images.forEach((image, index) => {
      const img = document.createElement('img');
      img.src = image.url;
      img.srcset = image.srcset;
      img.alt = image.alt || '';
      img.classList.add('home-hero__image');
      img.loading = 'eager';
      img.fetchPriority = 'high';

      // Only first image is visible initially
      if (index !== 0) {
        img.style.opacity = '0';
      }

      this.floatingImage.appendChild(img);
      this.imageElements.push(img);
    });
  }

  setRandomPosition() {
    if (!this.floatingImage) return;

    // Set random initial position within the section bounds
    const rect = this.getBoundingClientRect();
    const imageWidth = this.floatingImage.offsetWidth;
    const imageHeight = this.floatingImage.offsetHeight;

    const maxX = rect.width - imageWidth;
    const maxY = rect.height - imageHeight;

    this.current.x = Math.random() * Math.max(0, maxX);
    this.current.y = Math.random() * Math.max(0, maxY);
    this.mouse.x = this.current.x;
    this.mouse.y = this.current.y;
  }

  /**
   * @param {MouseEvent} e
   */
  onMouseMove(e) {
    this.updatePosition(e.clientX, e.clientY);
  }

  /**
   * @param {TouchEvent} e
   */
  onTouchMove(e) {
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      this.updatePosition(touch.clientX, touch.clientY);
    }
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  updatePosition(clientX, clientY) {
    if (!this.floatingImage) return;

    // Update position relative to the component
    const rect = this.getBoundingClientRect();
    const imageWidth = this.floatingImage.offsetWidth;
    const imageHeight = this.floatingImage.offsetHeight;

    // Calculate target position (centered on pointer)
    const targetX = clientX - rect.left - imageWidth / 2;
    const targetY = clientY - rect.top - imageHeight / 2;

    this.mouse.x = targetX;
    this.mouse.y = targetY;
  }

  /**
   * @param {number} start
   * @param {number} end
   * @param {number} factor
   */
  lerp(start, end, factor) {
    return start + (end - start) * factor;
  }

  animateLoop() {
    if (!this.floatingImage) return;

    // Smoothly interpolate current position towards mouse position
    this.current.x = this.lerp(this.current.x, this.mouse.x, this.lerpFactor);
    this.current.y = this.lerp(this.current.y, this.mouse.y, this.lerpFactor);

    // Update image position
    this.floatingImage.style.transform = `translate(${this.current.x}px, ${this.current.y}px)`;

    // Continue animation loop
    this.animationFrameId = requestAnimationFrame(this.animateLoop);
  }

  shuffleImage() {
    if (this.images.length <= 1) return;

    // Get next random image (different from current)
    let nextIndex;
    do {
      nextIndex = Math.floor(Math.random() * this.images.length);
    } while (nextIndex === this.currentImageIndex && this.images.length > 1);

    this.currentImageIndex = nextIndex;
    this.setImage(this.currentImageIndex);
  }

  /**
   * @param {number} index
   */
  setImage(index) {
    if (index < 0 || index >= this.imageElements.length) return;

    // Fade out all images
    this.imageElements.forEach((img) => {
      img.style.opacity = '0';
    });

    // Fade in the selected image
    this.imageElements[index].style.opacity = '1';
  }
  checkAnnouncement() {
    const announcementBar = document.querySelector('.announcement-bar');
    if (announcementBar) {
      this.classList.add('has-announcement');
    } else {
      this.classList.remove('has-announcement');
    }
  }

  disconnectedCallback() {
    // Clean up
    document.removeEventListener('mousemove', this.onMouseMove);
    this.removeEventListener('touchmove', this.onTouchMove);
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.shuffleIntervalId) {
      clearInterval(this.shuffleIntervalId);
    }

    if (Shopify?.designMode) {
      document.removeEventListener('shopify:section:load', this.checkAnnouncement);
      document.removeEventListener('shopify:section:unload', this.checkAnnouncement);
    }
  }
}

customElements.define('home-hero-component', HomeHeroComponent);
